/**
 * PURPOSE:
 * Orquestar la vista pública del deporte y sus tabs sin mezclar la UI con los consumos HTTP.
 *
 * RESPONSIBILITIES:
 * - Cargar evento, deporte y config pública mediante services.
 * - Resolver las tabs visibles según el tipo de deporte.
 * - Mantener el tracking de analytics alineado con las tabs realmente renderizadas.
 * - Delegar el catálogo/configuración pública de tabs a utils/publicSportTabs.jsx.
 *
 * COLLABORATORS:
 * - src/services/sportSection.js
 * - src/utils/publicSportTabs.jsx
 * - componentes públicos renderizados por cada tab
 *
 * NOTES:
 * - Colectivos y Colectivos Express mantienen tabs fijos por typeSport.
 * - Individuales pueden configurar tabs desde config_category.tabs_config_publico.
 * - Si un individual no tiene tabs_config_publico válido, cae al default:
 *   Equipos, Información, Programación, Resultados.
 *
 * ROUTE:
 * src/sections/SportSection.jsx
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import styled from "@emotion/styled";
import { IoChevronBack } from "react-icons/io5";

import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import "./SportSection.css";

import { trackEvent } from "../analytics.js";
import {
  getChampionshipByEvent,
  getPublicSportConfig,
  getSportById,
} from "../services/sportSection.js";
import {
  PUBLIC_TAB_CODES,
  buildSportSectionTabs,
  isKarateHayllisSection,
} from "../utils/publicSportTabs.jsx";

const CustomTabList = styled(TabList)`
  .MuiTab-root {
    color: #616161;
    font-weight: bold;
    text-transform: uppercase;
    transition: color 0.3s;
    &:hover {
      color: #1565c0;
    }
    &.Mui-selected {
      color: #1e88e5;
    }
  }
  .MuiTabs-indicator {
    background-color: #1e88e5;
    height: 4px;
  }
`;

const CustomTabPanel = styled(TabPanel)`
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
`;

/** Render helper reutilizable para tabs visibles e icon-only. */
const renderTabItems = (tabs, className, iconOnly = false) =>
  tabs
    .filter((tab) => tab.showInTabList !== false)
    .map((tab) => (
      <Tab
        key={`${className}-${tab.value}`}
        icon={tab.icon}
        label={iconOnly ? undefined : tab.label}
        value={tab.value}
        className={className}
      />
    ));

const SportSection = () => {
  const [value, setValue] = useState(null);
  const { idevent, idsport } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sportBanner, setSportBanner] = useState("");
  const [nameSport, setNameSport] = useState("");
  const [event, setEvent] = useState(null);
  const [sport, setSport] = useState(null);
  const [publicConfig, setPublicConfig] = useState(null);

  const ideventNumber = Number(idevent);
  const idsportNumber = Number(idsport);
  const idinstitution = searchParams.get("idinstitution");
  const navigate = useNavigate();
  const isCurrentKarateHayllisSection = isKarateHayllisSection(
    ideventNumber,
    idsportNumber
  );

  const handleInstitutionClick = useCallback((institutionId) => {
    setSearchParams({ idinstitution: institutionId });
  }, [setSearchParams]);

  const tabDefinitions = useMemo(() => {
    if (!sport) {
      return [];
    }

    return buildSportSectionTabs({
      sportType: Number(sport.typeSport),
      ideventNumber,
      idsportNumber,
      idinstitution,
      handleInstitutionClick,
      tabsConfigPublico: publicConfig?.tabs_config_publico,
    });
  }, [
    sport,
    ideventNumber,
    idsportNumber,
    idinstitution,
    handleInstitutionClick,
    publicConfig?.tabs_config_publico,
  ]);

  const visibleTabLabels = useMemo(() => {
    return tabDefinitions.reduce((acc, tab) => {
      if (tab.showInTabList === false || !tab.label) {
        return acc;
      }

      acc[tab.value] = tab.label;
      return acc;
    }, {});
  }, [tabDefinitions]);

  const handleChange = (_event, newValue) => {
    setValue(newValue);

    trackEvent({
      category: "Pestañas",
      action: "Seleccionar pestaña",
      label: visibleTabLabels[newValue] || newValue,
    });
  };

  useEffect(() => {
    let isMounted = true;

    const loadSportSectionData = async () => {
      const [eventResult, sportResult, configResult] = await Promise.allSettled([
        getChampionshipByEvent(idevent),
        getSportById(idsport),
        getPublicSportConfig({ idevent, idsport }),
      ]);

      if (!isMounted) {
        return;
      }

      if (eventResult.status === "fulfilled") {
        setEvent(eventResult.value);
      } else {
        console.error("Error fetching event", eventResult.reason);
      }

      if (sportResult.status === "fulfilled") {
        setSport(sportResult.value);
      } else {
        console.error("Error fetching sport", sportResult.reason);
      }

      if (configResult.status === "fulfilled") {
        const config = configResult.value;
        setPublicConfig(config || null);
        setSportBanner(config?.sport_banner || "");
        setNameSport(config?.nameSport || "");
        setValue(String(config?.defaultTabLinkPublico || "1"));
      } else {
        console.error("Error fetching config", configResult.reason);
        setPublicConfig(null);
        setValue("1");
      }
    };

    if (idevent && idsport) {
      loadSportSectionData();
    }

    return () => {
      isMounted = false;
    };
  }, [idevent, idsport]);

  useEffect(() => {
    if (!tabDefinitions.length || value === null) {
      return;
    }

    const allowedTabValues = tabDefinitions.map((tab) => String(tab.value));
    if (!allowedTabValues.includes(String(value))) {
      setValue(String(tabDefinitions[0].value));
    }
  }, [tabDefinitions, value]);

  if (value === null) {
    return <div>Cargando configuración...</div>;
  }

  return (
    <div
      className={`container_deporte_colectivo flex w-full max-w-full flex-col items-center ${isCurrentKarateHayllisSection ? "min-w-0 overflow-hidden" : ""
        }`}
      style={{ marginTop: "75px" }}
    >
      <div
        className={
          isCurrentKarateHayllisSection
            ? "w-full max-w-full min-w-0 overflow-hidden"
            : undefined
        }
      >
        <div className="text-center">
          {sportBanner && (
            <div className="w-full flex justify-center mb-4">
              <img
                src={sportBanner}
                alt="Banner"
                className="w-full sm:w-full md:w-11/12 lg:w-11/12 xl:w-11/12 h-auto object-cover"
              />
            </div>
          )}

          <h2 className="text-2xl sm:text-4xl font-bold text-blue-600">
            {event?.name}
          </h2>
          <div>
            <h3 className="group flex justify-center items-center text-3xl sm:text-5xl font-extrabold text-gray-900 mt-4">
              <IoChevronBack
                className="cursor-pointer mr-2 group-hover:-translate-x-1 transition-transform duration-150 ease-out"
                onClick={() => navigate(-1)}
              />
              {nameSport || sport?.name}
            </h3>
          </div>
        </div>

        <div className="mt-4 w-full max-w-full min-w-0 overflow-hidden font-quickSand">
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: isCurrentKarateHayllisSection ? "hidden" : undefined,
              typography: "body1",
            }}
          >
            <TabContext value={value}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <CustomTabList
                  onChange={handleChange}
                  aria-label="tabs deporte público"
                  centered
                >
                  {renderTabItems(tabDefinitions, "tab-item")}
                  {renderTabItems(tabDefinitions, "tab-item2", true)}
                </CustomTabList>
              </Box>

              {tabDefinitions.map((tab) => (
                <CustomTabPanel
                  key={`panel-${tab.value}`}
                  value={tab.value}
                  className={
                    isCurrentKarateHayllisSection &&
                      String(tab.value) === PUBLIC_TAB_CODES.CATEGORIAS
                      ? "karate-hayllis-tab-panel"
                      : undefined
                  }
                >
                  {tab.panel}
                </CustomTabPanel>
              ))}
            </TabContext>
          </Box>
        </div>
      </div>
    </div>
  );
};

export default SportSection;
